import * as THREE from 'three';
import { mergeVertices } from 'three/examples/jsm/utils/BufferGeometryUtils.js';
import { createRandom } from '../core/random.js';

/**
 * Monstruo verde low-poly: bestia encorvada que galopa apoyándose en sus largos brazos.
 * Su lomo (≈1,5 m) queda por debajo de la línea de visión cámara → jugador, así que cuando
 * se acerca se le ve detrás sin tapar al corredor. Mira hacia -Z; origen en el suelo.
 */
const rand = createRandom(777);
const mat = (color, extra) => new THREE.MeshLambertMaterial({ color, flatShading: true, ...extra });

/** Icosaedro deformado (aspecto orgánico). */
function blob(radius, detail, amount, sx, sy, sz) {
  const g = new THREE.IcosahedronGeometry(radius, detail);
  g.deleteAttribute('normal');
  g.deleteAttribute('uv');
  const m = mergeVertices(g);
  const p = m.attributes.position;
  for (let i = 0; i < p.count; i++) {
    const f = 1 + (rand() - 0.5) * amount;
    p.setXYZ(i, p.getX(i) * f * sx, p.getY(i) * f * sy, p.getZ(i) * f * sz);
  }
  m.computeVertexNormals();
  return m;
}

function part(geom, material, x = 0, y = 0, z = 0) {
  const m = new THREE.Mesh(geom, material);
  m.position.set(x, y, z);
  return m;
}

function joint(parent, x, y, z) {
  const g = new THREE.Group();
  g.position.set(x, y, z);
  parent.add(g);
  return g;
}

export class Monster {
  constructor() {
    const skin = mat(0x3f9a37);
    const dark = mat(0x1f5a1c);
    const belly = mat(0x8fc46a);
    const bone = mat(0xeee6c8);
    this.eyes = mat(0xffee55, { emissive: 0xffaa00, emissiveIntensity: 1 });

    this.root = new THREE.Group();
    this.body = joint(this.root, 0, 0, 0);
    this.body.add(part(blob(0.8, 1, 0.25, 1.05, 0.62, 0.95), skin, 0, 0.85, 0));
    this.body.add(part(blob(0.5, 1, 0.2, 1.2, 0.6, 0.9), belly, 0, 0.62, -0.35));
    // Púas del lomo: es lo que ve la cámara cuando lo tiene detrás del jugador.
    for (let i = 0; i < 5; i++) {
      const spike = part(new THREE.ConeGeometry(0.12 + rand() * 0.06, 0.4 + rand() * 0.2, 4), dark, (i % 2 ? 0.18 : -0.18) * (i > 0), 1.3 - Math.abs(i - 1.5) * 0.04, -0.35 + i * 0.22);
      spike.rotation.x = 0.5;
      this.body.add(spike);
    }

    // Cabeza con mandíbula articulada
    this.head = joint(this.body, 0, 0.98, -0.75);
    this.head.add(part(blob(0.42, 1, 0.2, 1.15, 0.7, 1), skin, 0, 0.05, -0.12));
    this.head.add(part(new THREE.BoxGeometry(0.62, 0.1, 0.22), dark, 0, 0.22, -0.32)); // ceño
    for (const side of [-1, 1]) {
      this.head.add(part(new THREE.IcosahedronGeometry(0.085, 0), this.eyes, side * 0.17, 0.13, -0.47));
      const horn = part(new THREE.ConeGeometry(0.07, 0.35, 5), bone, side * 0.3, 0.32, -0.05);
      horn.rotation.set(0.6, 0, -side * 0.5);
      this.head.add(horn);
    }
    for (let i = 0; i < 6; i++) {
      const tooth = part(new THREE.ConeGeometry(0.035, 0.12, 4), bone, -0.22 + i * 0.088, -0.1, -0.5);
      tooth.rotation.x = Math.PI;
      this.head.add(tooth);
    }
    this.jaw = joint(this.head, 0, -0.1, -0.05);
    this.jaw.add(part(new THREE.BoxGeometry(0.52, 0.12, 0.45), skin, 0, -0.06, -0.22));
    for (let i = 0; i < 5; i++) this.jaw.add(part(new THREE.ConeGeometry(0.035, 0.11, 4), bone, -0.18 + i * 0.09, 0.04, -0.42));

    // Brazos largos con garras (galopa con ellos)
    this.arms = [-1, 1].map((side) => {
      const shoulder = joint(this.body, side * 0.82, 1.0, -0.35);
      shoulder.add(part(new THREE.CylinderGeometry(0.17, 0.13, 0.55, 6).translate(0, -0.27, 0), skin));
      const elbow = joint(shoulder, 0, -0.55, 0);
      elbow.add(part(new THREE.CylinderGeometry(0.13, 0.11, 0.5, 6).translate(0, -0.25, 0), dark));
      for (let c = -1; c <= 1; c++) {
        const claw = part(new THREE.ConeGeometry(0.04, 0.18, 4), bone, c * 0.07, -0.55, -0.06);
        claw.rotation.x = -Math.PI * 0.8;
        elbow.add(claw);
      }
      return { shoulder, elbow };
    });
    // Patas traseras cortas
    this.legs = [-1, 1].map((side) => {
      const hip = joint(this.body, side * 0.5, 0.6, 0.45);
      hip.add(part(new THREE.CylinderGeometry(0.2, 0.14, 0.42, 6).translate(0, -0.2, 0), dark));
      hip.add(part(new THREE.BoxGeometry(0.26, 0.12, 0.38), dark, 0, -0.42, -0.08));
      return hip;
    });
    this.phase = 0;
  }

  /** Galope: brazos a la vez y patas al contrario; la boca se abre más cuando está cerca. */
  update(dt, speed, near, pounce = false) {
    // Al atrapar al jugador se abalanza: se alza sobre las patas y abre los brazos.
    this.pounce = pounce ? Math.min(1, (this.pounce ?? 0) + dt * 4) : 0;
    if (this.pounce > 0) {
      const k = this.pounce;
      this.body.rotation.x = -0.5 * k;
      this.body.position.y = 0.5 * Math.sin(k * Math.PI * 0.5);
      for (const a of this.arms) a.shoulder.rotation.x = 0.35 + 1.6 * k;
      this.jaw.rotation.x = -0.7 * k;
      this.eyes.emissiveIntensity = 2.2;
      return;
    }
    this.phase += dt * speed * 0.75;
    const s = Math.sin(this.phase);
    for (const a of this.arms) {
      a.shoulder.rotation.x = 0.35 + s * 0.85;
      a.elbow.rotation.x = -0.3 - Math.max(0, s) * 0.6;
    }
    for (const l of this.legs) l.rotation.x = -s * 0.7;
    this.body.rotation.x = s * 0.1;
    this.body.position.y = Math.abs(Math.sin(this.phase)) * 0.14;
    this.head.rotation.x = -s * 0.12;
    this.jaw.rotation.x = -0.15 - (near ? 0.35 : 0.1) * (0.6 + 0.4 * Math.sin(this.phase * 2));
    this.eyes.emissiveIntensity = near ? 1.6 + Math.sin(this.phase * 3) * 0.4 : 1;
  }
}
